import { useCallback, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginatedData } from 'models/PaginatedData';
import { WaitingRoomRequest } from 'models/WaitingRoom';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { getMixpanelErrorProps } from 'axiosUtils';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { WaitingListUpdateEventData } from 'services/socketService/socketEventData';
import dataRoomApi from '../dataRoomApi';
import {
  WaitingRoomFilterOptions,
  WaitingRoomParams,
} from '../dataRoomApi/waiting-room';
import DataRoomContext from '../providers/DataRoom/DataRoom.context';

export const WAITING_ROOM_QUERY_KEY = 'waiting-room-requests';
export const WAITING_ROOM_COUNT_QUERY_KEY = 'waiting-room-requests-count';
export const WAITING_ROOM_FILTER_OPTIONS_QUERY_KEY =
  'waiting-room-requests-filter-options';

const EMPTY_REQUESTS: WaitingRoomRequest[] = [];

const EMPTY_FILTER_OPTIONS: WaitingRoomFilterOptions = {
  requestors: [],
  emails: [],
  requestingAccessTo: [],
};

// Approving, declining or a request arriving over the socket changes the page
// the table is on, the badge total and which values are left to filter by, so
// the three queries are always invalidated together.
export function useInvalidateWaitingRoom(dataRoomId: string) {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [WAITING_ROOM_QUERY_KEY, dataRoomId],
    });

    queryClient.invalidateQueries({
      queryKey: [WAITING_ROOM_COUNT_QUERY_KEY, dataRoomId],
    });

    queryClient.invalidateQueries({
      queryKey: [WAITING_ROOM_FILTER_OPTIONS_QUERY_KEY, dataRoomId],
    });
  }, [dataRoomId, queryClient]);
}

function useWaitingRoomSocket(dataRoomId: string) {
  const invalidate = useInvalidateWaitingRoom(dataRoomId);

  const onWaitingRoomUpdated = useCallback(
    ({ dataRoomId: eventDataRoomId }: WaitingListUpdateEventData) => {
      if (dataRoomId !== eventDataRoomId) return;

      invalidate();
    },
    [dataRoomId, invalidate],
  );

  useSocketEvent(LISTEN_EVENTS.WAITING_ROOM_UPDATED, onWaitingRoomUpdated);
}

// One page of the queue, sorted and filtered by the BE. Every param the table
// drives is part of the cache key, so paging back and forth is served from
// cache rather than refetched.
export function useWaitingRoomQuery(params: WaitingRoomParams) {
  const {
    dataRoom: { _id: dataRoomId },
    canUserCreateShareableLinks,
  } = useContext(DataRoomContext);

  const getMixpanelProps = useGetMixpanelProps();

  useWaitingRoomSocket(dataRoomId);

  const query = useQuery<PaginatedData<WaitingRoomRequest>>({
    queryKey: [WAITING_ROOM_QUERY_KEY, dataRoomId, params],
    queryFn: async () => {
      const { data } = await dataRoomApi.waitingRoom.getWaitingRoom(
        dataRoomId,
        params,
      );

      return data;
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    // Only the people who can moderate the room have access to the queue.
    enabled: !!dataRoomId && canUserCreateShareableLinks,
    // Changing page or sort keeps the current rows on screen until the next
    // ones land, instead of collapsing the table to an empty state.
    keepPreviousData: true,
  });

  const { data, isLoading, isFetching } = query;

  return useMemo(
    () => ({
      requests: data?.items ?? EMPTY_REQUESTS,
      totalPages: data?.totalPages ?? 0,
      // How many requests match the current filters — the table's total, not
      // the badge's. Use `useWaitingRoomCount` for the badge.
      filteredCount: data?.totalItems ?? 0,
      isLoading,
      isFetching,
    }),
    [data, isFetching, isLoading],
  );
}

// The values the queue can be filtered by. Not filtered itself, so narrowing
// the table never narrows what is left to pick from.
export function useWaitingRoomFilterOptions() {
  const {
    dataRoom: { _id: dataRoomId },
    canUserCreateShareableLinks,
  } = useContext(DataRoomContext);

  const getMixpanelProps = useGetMixpanelProps();

  const { data } = useQuery<WaitingRoomFilterOptions>({
    queryKey: [WAITING_ROOM_FILTER_OPTIONS_QUERY_KEY, dataRoomId],
    queryFn: async () => {
      const { data } =
        await dataRoomApi.waitingRoom.getWaitingRoomFilterOptions(dataRoomId);

      return data;
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled: !!dataRoomId && canUserCreateShareableLinks,
  });

  return data ?? EMPTY_FILTER_OPTIONS;
}

// Everyone waiting, whatever the table is filtered to. Its own cache entry
// precisely so the table's filters cannot reach it.
export function useWaitingRoomCount() {
  const {
    dataRoom: { _id: dataRoomId },
    canUserCreateShareableLinks,
  } = useContext(DataRoomContext);

  const getMixpanelProps = useGetMixpanelProps();

  useWaitingRoomSocket(dataRoomId);

  const { data } = useQuery<number>({
    queryKey: [WAITING_ROOM_COUNT_QUERY_KEY, dataRoomId],
    queryFn: async () => {
      const {
        data: { totalItems },
      } = await dataRoomApi.waitingRoom.getWaitingRoomCount(dataRoomId);

      return totalItems;
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled: !!dataRoomId && canUserCreateShareableLinks,
  });

  return data ?? 0;
}
